/**
 * Panels 1 and 5 — the two fixture lists (§B4 and §B9).
 *
 * One component draws both, and the `dataset` prop is the only thing that differs. That is
 * not a shortcut: Part C invariant 5 says the two datasets must never cross, and the surest
 * way to keep two tables reading the same four slots the same way is for there to be one
 * table that is told which half it is looking at.
 *
 * What the prop actually changes:
 *
 *  * **`tmsActual` cells are plain text, `aiReference` cells are buttons.** §B4: *"Umpire
 *    cells are plain text, not buttons — the page is structurally incapable of editing them."*
 *    The read-only half renders no control at all rather than a disabled one, because a
 *    disabled button is a control that exists and is currently refused, and this one never
 *    exists.
 *  * **The last column.** §B4 heads it `Rule Check` and prints `Passes` or `{n} Violation(s)`;
 *    §B9 heads it `Status` and prints §B9's two pills, or the violation count where a what-if
 *    edit has created one.
 *  * **The head controls and the foot.** `Load Appointments` against `Draft All` and
 *    `Clear Drafts`, and each panel's own verbatim foot.
 *
 * The violation chip is drawn from the server's own `violation` caption on the cell, in both
 * halves. Nothing on this side decides what a violation is (Part C invariant 1).
 */

import { Fragment, useRef } from "react";

import type { UmpireSlot } from "@hockeytd/shared";

import type { UmpireFixtureBody, UmpireSlotBody } from "../../api/types.js";
import { COMPETITION_TABS, STAGE_TABS, Tabs } from "../appointments/Tabs.js";
import type { CompetitionFilter, StageFilter } from "../appointments/derive.js";
import { dayGroups, dayLabel, dayNumbers, useLandOnDay } from "../schedule/matchDays.js";
import { slotsOf, type UmpireDataset } from "./derive.js";

/**
 * RUN 81B — the `Show Stored Draft` button's id, defined beside the button that carries it and
 * read by the panel, so a successful retry knows whether the focus is still on it.
 */
export const SHOW_STORED_DRAFT_ID = "showStoredDraft";

export function umpireRowKey(tmsMatchDbId: number, slot: UmpireSlot): string {
  return `${String(tmsMatchDbId)}:${slot}`;
}

export interface UmpireFixtureListPanelProps {
  dataset: UmpireDataset;
  /** Already narrowed to this panel's own tabs. */
  fixtures: readonly UmpireFixtureBody[];
  /** Everything the fetch brought back, so an empty view can say which kind of empty it is. */
  allFixtures: readonly UmpireFixtureBody[];
  competition: CompetitionFilter;
  onCompetition: (value: CompetitionFilter) => void;
  stage: StageFilter;
  onStage: (value: StageFilter) => void;
  busy: boolean;
  /** TMS half only. */
  onFetch?: () => void;
  fetching?: boolean;
  /**
   * AI half only — RUN 38's two presses, mirroring the TO page's pair.
   *
   * `Draft The Pool Stage` covers the pool matches, which is exactly what the single
   * `Draft All` covered before this run; the new `Draft All` covers the whole current
   * schedule. Both ask before they write.
   */
  onDraftPool?: () => void;
  draftingPool?: boolean;
  onDraftAll?: () => void;
  draftingAll?: boolean;
  /**
   * AI half only — RUN 40's `Re-Draft`, the TO page's control brought across.
   *
   * It opens a confirmation rather than drafting, exactly as its two neighbours do, and it is
   * offered only once the advisory half holds something: re-drafting nothing is a press that
   * cannot mean anything, and the gate is deliberately the SAME condition that decides whether
   * `Export AI Reference` is offered (`exportable`), so the head cannot be in a state where the
   * page believes there is a draft to export but not one to redraw.
   */
  onReDraft?: () => void;
  redrafting?: boolean;
  onClearDrafts?: () => void;
  clearing?: boolean;
  /** AI half only — which cell the picker is open on. */
  openSlot?: { tmsMatchDbId: number; slot: UmpireSlot } | null;
  onOpenPicker?: (
    fixture: UmpireFixtureBody,
    slot: UmpireSlotBody,
    anchor: HTMLElement,
  ) => void;
  savingRow?: string | null;
  slotError?: { row: string; message: string } | null;
  /**
   * RUN 37 — the day this half scrolls itself to on the first draw, or `null` for the top.
   *
   * The two lists used to be paged twenty at a time, which meant that finding Thursday's
   * matches was three presses of `›` and remembering which page they had been on. They are now
   * one scrolling table each, with a separator at every date, landing on today. The same
   * `features/schedule/matchDays.ts` decides "today" here as on the TO page, because the one
   * thing worse than two fixture lists is two that disagree about which day it is.
   */
  landOn?: string | null;
  /**
   * §B11's Export, one per half. The two are different documents with different warnings on
   * them, which is why there is a button on each list rather than one button and a choice.
   */
  onExport?: () => void;
  exporting?: boolean;
  /**
   * False where there is nothing honest to export: the TMS half before `Load Appointments` has
   * been pressed this session, and the advisory half before anything has been drafted.
   */
  exportable?: boolean;
  /**
   * RUN 81 — AI half only. Where the read of the stored advisory draft has got to (§Panel 5a).
   *
   * The page reads the draft this system already holds as it opens. Until that read has
   * answered, this list has nothing to show and nothing it may safely offer to write, so its
   * empty state says which of the three it is — and none of the three tells the TD to draft,
   * because a draft press is the one thing that would replace a stored draft he has not seen.
   *
   * Omitted, it is `ready`, which is what a list handed its fixtures directly is.
   */
  loadState?: "loading" | "failed" | "ready";
  /** RUN 81 — AI half only. Why the stored draft could not be read, in the API's own words. */
  loadFailure?: string | null;
  /** RUN 81 — AI half only. `Show Stored Draft`: the same read again, after one that failed. */
  onShowStoredDraft?: () => void;
  /**
   * RUN 81A — AI half only. `Show Stored Draft` has been pressed and its read has not answered.
   * The button stays where it is, so the focus on it is not lost, and says it is reading.
   */
  loadingAgain?: boolean;
  /** RUN 81B — failures so far; keys the alert so a repeated refusal is announced again. */
  loadAttempts?: number;
  /**
   * RUN 81A — TMS half only. The page is reading the stored record as it opens: `Load Appointments`
   * is asleep and says so, and the empty state does not tell the TD to press it yet.
   */
  opening?: boolean;
  /**
   * RUN 81 — AI half only. Whether this system holds an advisory cell anywhere in the schedule.
   *
   * Only an explicit `false` puts the drafting note above the table, and that note is the one
   * place this list may tell the TD to draft. Omitted, the list says nothing about drafting.
   */
  storedDraft?: boolean;
}

export function UmpireFixtureListPanel(props: UmpireFixtureListPanelProps): React.JSX.Element {
  const {
    dataset,
    fixtures,
    allFixtures,
    competition,
    onCompetition,
    stage,
    onStage,
    busy,
    onFetch,
    fetching = false,
    onDraftPool,
    draftingPool = false,
    onDraftAll,
    draftingAll = false,
    onReDraft,
    redrafting = false,
    onClearDrafts,
    clearing = false,
    onExport,
    exporting = false,
    exportable = false,
    loadState = "ready",
    loadFailure = null,
    onShowStoredDraft,
    storedDraft,
    loadingAgain = false,
    opening = false,
    loadAttempts = 0,
  } = props;

  const tms = dataset === "tmsActual";
  const title = tms ? "Fixture List" : "AI Drafted Fixture List";
  const id = tms ? "umpFixTitle" : "aiFixTitle";

  const half = tms ? "tms" : "ai";

  /* RUN 37 — the whole schedule in one pane, with the day it opens at. */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useLandOnDay(scrollRef, props.landOn ?? null);

  const groups = dayGroups(fixtures);
  const numbers = dayNumbers(allFixtures.map((fixture) => fixture.date));

  return (
    <section
      className={`panel ${tms ? "panel--d1" : "panel--ai"}`}
      aria-labelledby={id}
      data-testid={tms ? "tms-fixture-list" : "ai-fixture-list"}
    >
      <div className="panel__head">
        {/* RUN 81A — the AI title can take the focus `Show Stored Draft` held once the stored
            draft arrives and the button goes. */}
        <h3 className="panel__title" id={id} tabIndex={tms ? undefined : -1}>
          {title}
        </h3>
        {tms ? (
          <button
            type="button"
            className="gbtn gbtn--solid"
            onClick={onFetch}
            disabled={busy}
            aria-busy={fetching || opening}
            data-howto="fetch-tms"
            data-testid="fetch-tms"
            title="Show the umpire appointments recorded in this system. This page does not read TMS."
          >
            {/* Decision #13 (§B4a) — it loads this system's record; it has never read TMS. */}
            {fetching ? "Loading…" : "Load Appointments"}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="gbtn gbtn--solid"
              onClick={onDraftAll}
              disabled={busy}
              aria-busy={draftingAll}
              data-howto="draft-all"
              data-testid="draft-all"
              title="Draft every match in the schedule — the pool stage and everything after it"
            >
              {draftingAll ? "Drafting…" : "Draft All"}
            </button>
            {/* The narrower of the two, and the one that does what this half has always done.
                It sits second because `Draft All` is the wider press and the TO page puts its
                widest control first; the confirmation on each says which matches it reaches, so
                the order decides nothing. */}
            <button
              type="button"
              className="gbtn gbtn--solid"
              onClick={onDraftPool}
              disabled={busy}
              aria-busy={draftingPool}
              data-howto="draft-pool-stage"
              data-testid="draft-pool-stage"
              title="Draft the pool matches only, leaving everything after the pool stage blank"
            >
              {draftingPool ? "Drafting The Pool Stage…" : "Draft The Pool Stage"}
            </button>
            {/* RUN 40 — the shuffle, brought across from the TO page in the same visual family
                as the two buttons beside it, because it IS one of those buttons with the
                engine's variant moved on: another arrangement, equally even and equally
                legal, with every cell the TD has adjusted by hand kept exactly as the plain
                draft presses keep them. It asks first, for the same reason they do.

                It appears only once there is a draft to redraw — the same condition that
                decides whether this half has anything to export. Nothing on the Umpires
                Managers' half has, or could have, a control like it. */}
            {!exportable ? null : (
              <button
                type="button"
                className="gbtn gbtn--solid"
                onClick={onReDraft}
                disabled={busy}
                aria-busy={redrafting}
                data-howto="redraft-umpires"
                data-testid="redraft-umpires"
                title="Draft the advisory half again, arranged differently. Cells you have edited by hand are kept."
              >
                {redrafting ? "Re-Drafting…" : "Re-Draft"}
              </button>
            )}
            <button
              type="button"
              className="gbtn gbtn--quiet"
              onClick={onClearDrafts}
              disabled={busy}
              aria-busy={clearing}
              data-howto="clear-drafts"
              data-testid="clear-drafts"
            >
              {clearing ? "Clearing…" : "Clear Drafts"}
            </button>
          </>
        )}
        <span className="panel__note">
          {tms ? "Read-Only — As Recorded In This System" : "Reference Only — Advisory What-If Editing"}
        </span>
        <span className="spacer" />
        <span className="filtlab">Competition</span>
        <Tabs
          label={tms ? "TMS competition filter" : "AI competition filter"}
          options={COMPETITION_TABS}
          value={competition}
          onChange={onCompetition}
          tint="competition"
        />
        <span className="filtlab">Stage</span>
        <Tabs
          label={tms ? "TMS stage filter" : "AI stage filter"}
          options={STAGE_TABS}
          value={stage}
          onChange={onStage}
        />
      </div>

      {/* RUN 81 — §Panel 5a. The one place this list tells the TD to draft, and only once the
          stored record has been read, the schedule has matches, and not one advisory cell is
          held anywhere in it. Over a stored draft a press would replace it, so it says nothing. */}
      {!tms && loadState === "ready" && storedDraft === false && allFixtures.length > 0 ? (
        <p className="aidraftnote" data-testid="ai-no-draft-note">
          No draft is stored yet — press Draft The Pool Stage, or Draft All for the whole
          schedule, to see the yardstick.
        </p>
      ) : null}

      <div
        className="fixwrap fixwrap--all"
        ref={scrollRef}
        data-testid={`fixlist-scroll-${half}`}
      >
        <table
          className="mtable fixtable umpfix"
          aria-label={
            tms ? "Umpire appointments as recorded in this system" : "AI drafted umpire appointments"
          }
        >
          <thead>
            <tr>
              <th scope="col">Match</th>
              <th scope="col">Date</th>
              <th scope="col">U1</th>
              <th scope="col">U2</th>
              <th scope="col" className="grp-video">
                RU
              </th>
              <th scope="col">VU</th>
              <th scope="col">{tms ? "Rule Check" : "Status"}</th>
            </tr>
          </thead>
          <tbody>
            {/* RUN 37 — one block per match day, each under its own separator row, which is
                also what the pane scrolls itself to on the first draw. */}
            {groups.map((group) => (
              <Fragment key={group.date}>
                <tr
                  className="fixdayrow"
                  data-testid={`fixlist-dayrow-${half}`}
                  data-day={group.date}
                >
                  <td colSpan={7}>
                    <span className="fixdayrow__date">{dayLabel(group.date)}</span>
                    {numbers.get(group.date) === undefined ? null : (
                      <span className="fixdayrow__no">— Day {numbers.get(group.date)}</span>
                    )}
                    <span className="fixdayrow__count num">
                      {group.fixtures.length} match{group.fixtures.length === 1 ? "" : "es"}
                    </span>
                  </td>
                </tr>
                {group.fixtures.map((fixture) => (
                  <UmpireFixtureRow
                    key={fixture.tmsMatchDbId}
                    fixture={fixture}
                    {...props}
                  />
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>

        {fixtures.length === 0 && tms ? (
          <div className="emptystate" data-testid="tms-fixtures-empty">
            <b>{allFixtures.length === 0 ? "Nothing Loaded Yet" : "No Matches In This View"}</b>
            {allFixtures.length > 0
              ? "Every match loaded sits outside the filters above. Widen them to see it."
              : opening
                ? null
                : "Press Load Appointments to show the umpire appointments recorded in this system. This page does not read TMS."}
          </div>
        ) : null}

        {/* RUN 81 — §Panel 5a. The AI half's empty state has four things it can be, and not one
            of them names a draft button: until the stored record has been read there may be a
            draft nobody has seen, and over a stored draft a press would replace it. The note
            above the table is the one place this list says to draft. */}
        {fixtures.length === 0 && !tms ? (
          <div className="emptystate" data-testid="ai-fixtures-empty">
            {allFixtures.length > 0 ? (
              <>
                <b>No Matches In This View</b>
                Every match in the schedule sits outside the filters above. Widen them to see it.
              </>
            ) : loadState === "loading" ? (
              /* RUN 81A — announced, as every other state change on this page is. */
              <div role="status">
                <b>Loading The Stored Draft</b>
                Reading the advisory draft this system already holds for the schedule.
              </div>
            ) : loadState === "failed" ? (
              <>
                <div role="alert" key={loadAttempts}>
                  <b>Stored Draft Not Loaded</b>
                  {loadFailure ?? "The stored draft could not be read."}
                </div>
                <div className="storedretry">
                  {/* RUN 81A — `aria-disabled` rather than `disabled` while its read runs, so the
                      focus stays on the button (the RUN 80A pattern). */}
                  <button
                    type="button"
                    className="gbtn gbtn--solid"
                    onClick={() => {
                      if (!loadingAgain) onShowStoredDraft?.();
                    }}
                    aria-disabled={loadingAgain}
                    aria-busy={loadingAgain}
                    id={SHOW_STORED_DRAFT_ID}
                    data-testid="show-stored-draft"
                  >
                    {loadingAgain ? "Reading The Stored Draft…" : "Show Stored Draft"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <b>No Matches In The Schedule</b>
                This system holds no matches for the schedule yet, so the advisory half has
                nothing to show.
              </>
            )}
          </div>
        ) : null}
      </div>

      <div className="panel__foot">
        {tms ? (
          <>
            <b>Red means a rule violation —</b> a name shown in{" "}
            <span className="swatch-red">Bold Red</span> breaks one of the appointment rules,
            checked automatically every time the appointments are loaded: <b>one person holding
            two roles in the same
            match</b>, or <b>an umpire appointed to a match involving their own country</b>. The
            role chip beside the name says which rule failed. You cannot correct it here — raise
            it with the Umpires Managers.
            <div className="hidenote">
              <b>Note —</b> An umpire may take at most one on-field duty (U1 or U2) per day,
              counted across both competitions, but may hold several reserve or video duties on
              the same day.
            </div>
          </>
        ) : (
          <>
            <b>Reference only —</b> there are deliberately no action buttons here beyond the
            draft itself. Umpire appointments belong to the Umpires Managers; this draft exists
            solely as a yardstick for how evenly duties <i>could</i> be spread, and nothing on
            it is ever pushed anywhere.
          </>
        )}
      </div>

      {/* RUN 37 — the row survives its pager. The `‹ ›` buttons are gone because the list no
          longer has pages: the TD asked to reach any day by scrolling, and a control that
          moved him a page at a time through a table he is already scrolling would be two ways
          of doing one thing. The count line and BOTH exports stay exactly where RUN 35 put
          them, and the exports still take the list as displayed — which is now every day of
          it. */}
      <div className="pager" aria-label={tms ? "Fixture list summary" : "AI fixture list summary"}>
        <span
          className="pager__info num"
          data-testid={tms ? "tms-fixture-pager" : "ai-fixture-pager"}
        >
          {fixtures.length === 0
            ? "No Matches In This View"
            : `${String(fixtures.length)} ${fixtures.length === 1 ? "Match" : "Matches"} Shown Across ${String(groups.length)} ${groups.length === 1 ? "Match Day" : "Match Days"}`}
        </span>
        {/* §A3 reserves Export for the pager row, and this list has one. The two halves are
            two documents — a record and an advisory draft — so each carries its own, tinted
            like the half it belongs to and disabled while that half holds nothing. */}
        <button
          type="button"
          className="exportbtn"
          data-testid={tms ? "export-umpires-tms" : "export-umpires-ai"}
          onClick={onExport}
          disabled={exporting || !exportable}
          aria-busy={exporting}
          title={
            tms
              ? "Download the Umpires Managers' appointments as displayed, with a timestamp"
              : "Download the advisory draft as displayed, labelled advisory, with a timestamp"
          }
        >
          {exporting ? "Exporting…" : tms ? "Export TMS Actual" : "Export AI Reference"}
        </button>
      </div>
    </section>
  );
}

/* ── One fixture ──────────────────────────────────────────────────────────── */

function UmpireFixtureRow({
  fixture,
  dataset,
  openSlot = null,
  onOpenPicker,
  savingRow = null,
  slotError = null,
  busy,
}: UmpireFixtureListPanelProps & { fixture: UmpireFixtureBody }): React.JSX.Element {
  const slots = slotsOf(fixture, dataset);

  return (
    <tr
      data-comp={fixture.competitionGender}
      data-testid={`${dataset === "tmsActual" ? "tms" : "ai"}-fixture-${fixture.tmsMatchDbId}`}
    >
      <td className="cell-match">
        <span className={`comp-tag${fixture.competitionGender === "w" ? " comp-tag--w" : ""}`}>
          {fixture.competitionGender === "w" ? "Women" : "Men"}
        </span>
        <span className="mno num">{fixture.displayMatchNo}</span>
        <span className="teams">
          {fixture.homeTeamCode ?? "TBC"} <i>v</i> {fixture.awayTeamCode ?? "TBC"}
        </span>
      </td>
      <td className="cell-date">
        {fixture.date}
        <span className="tm num">
          {fixture.time}
          {fixture.pitch === null ? "" : ` · ${fixture.pitch}`}
        </span>
      </td>

      {slots.map((slot) => (
        <UmpireSlotCell
          key={slot.slot}
          fixture={fixture}
          slot={slot}
          dataset={dataset}
          open={
            openSlot?.tmsMatchDbId === fixture.tmsMatchDbId && openSlot.slot === slot.slot
          }
          onOpenPicker={onOpenPicker}
          saving={savingRow === umpireRowKey(fixture.tmsMatchDbId, slot.slot)}
          error={
            slotError?.row === umpireRowKey(fixture.tmsMatchDbId, slot.slot)
              ? slotError.message
              : null
          }
          busy={busy}
        />
      ))}

      <td className="cell-status">
        <StatusCell fixture={fixture} dataset={dataset} />
      </td>
    </tr>
  );
}

/**
 * §B4's `Rule Check` pill, and §B9's `Status` pill.
 *
 * A violation outranks everything on both halves: a `TD Modified` chip over a red cell would
 * be telling the TD the least useful true thing on the row.
 */
function StatusCell({
  fixture,
  dataset,
}: {
  fixture: UmpireFixtureBody;
  dataset: UmpireDataset;
}): React.JSX.Element {
  const testId = `${dataset === "tmsActual" ? "tms" : "ai"}-status-${fixture.tmsMatchDbId}`;
  const pill = umpireRowStatus(fixture, dataset);
  return (
    <span className={pill.className} data-testid={testId}>
      <span className="sdot" aria-hidden="true" />
      {pill.label}
    </span>
  );
}

/**
 * What that pill says, as a pure function — because §B11's `Rule Check` and the advisory
 * sheet's `Status` are the same two verdicts, and a sheet that disagreed with the row it was
 * taken from would be the export quietly re-deciding what a violation is.
 */
export function umpireRowStatus(
  fixture: UmpireFixtureBody,
  dataset: UmpireDataset,
): { label: string; className: string } {
  const slots = slotsOf(fixture, dataset);
  const violations = slots.filter((slot) => slot.violation !== null).length;

  if (violations > 0) {
    return {
      label: `${String(violations)} ${violations === 1 ? "Violation" : "Violations"}`,
      className: "statuspill statuspill--alert",
    };
  }

  if (dataset === "tmsActual") {
    return slots.some((slot) => slot.tmsPersonId !== null)
      ? { label: "Passes", className: "statuspill statuspill--good" }
      : { label: "Not Appointed", className: "statuspill statuspill--muted" };
  }

  if (slots.some((slot) => slot.status === "td_modified")) {
    return { label: "TD Modified", className: "statuspill statuspill--warn" };
  }
  if (slots.some((slot) => slot.status === "ai_drafted")) {
    return { label: "AI Drafted", className: "statuspill" };
  }
  return { label: "Not Drafted", className: "statuspill statuspill--muted" };
}

/* ── One slot ─────────────────────────────────────────────────────────────── */

function UmpireSlotCell({
  fixture,
  slot,
  dataset,
  open,
  onOpenPicker,
  saving,
  error,
  busy,
}: {
  fixture: UmpireFixtureBody;
  slot: UmpireSlotBody;
  dataset: UmpireDataset;
  open: boolean;
  onOpenPicker:
    | ((fixture: UmpireFixtureBody, slot: UmpireSlotBody, anchor: HTMLElement) => void)
    | undefined;
  saving: boolean;
  error: string | null;
  busy: boolean;
}): React.JSX.Element {
  const half = dataset === "tmsActual" ? "tms" : "ai";
  const testId = `${half}-slot-${String(fixture.tmsMatchDbId)}-${slot.slot}`;
  const classes = [
    "slotcell",
    slot.violation === null ? "" : "is-violation",
    slot.slot === "RU" ? "grp-video" : "",
  ]
    .filter((entry) => entry !== "")
    .join(" ");

  const chip =
    slot.violation === null ? null : (
      <span className="vchip" data-testid={`${half}-vchip-${String(fixture.tmsMatchDbId)}-${slot.slot}`}>
        <span className="r">{slot.slot}</span>
        <span className="mk" aria-hidden="true">
          ⚠️
        </span>
        {slot.violation}
      </span>
    );

  return (
    <td className={classes} data-testid={testId}>
      {dataset === "tmsActual" || onOpenPicker === undefined ? (
        /* §B4 — plain text, and no control at all. The read-only half is read-only by
           construction rather than by a disabled button somebody could later enable. */
        <>
          <span className="umpname">{slot.mediaName ?? "—"}</span>
          <span className="umpnat">{slot.country ?? "Not Appointed"}</span>
        </>
      ) : (
        <button
          type="button"
          className={`slotbtn${slot.mediaName === null ? " is-empty" : ""}${saving ? " is-saving" : ""}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={
            slot.mediaName === null
              ? `Draft an umpire into the ${fixture.displayMatchNo} ${slot.slot} slot`
              : `Change ${slot.mediaName} in the ${fixture.displayMatchNo} ${slot.slot} slot`
          }
          data-testid={`ai-slot-name-${String(fixture.tmsMatchDbId)}-${slot.slot}`}
          disabled={busy && !saving}
          onClick={(event) => {
            onOpenPicker(fixture, slot, event.currentTarget);
          }}
        >
          <span className="umpname">{slot.mediaName ?? "Unfilled"}</span>
          <span className="umpnat">{slot.country ?? "Choose an umpire"}</span>
        </button>
      )}
      {chip}
      {error === null ? null : (
        <span className="rowerror" role="alert">
          {error}
        </span>
      )}
    </td>
  );
}
